// =============================================================================
// AttachmentPreview.tsx — Strip showing pending attachments (web port)
// =============================================================================

import React from 'react';
import { Box, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme } from '../../context/PermissionsContext/ThemeContext/ThemeContext';
import type { AttachmentItem } from '../../services/notesToHank/NotesToHankTypes';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface AttachmentPreviewProps {
  attachments: AttachmentItem[];
  onRemove: (index: number) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AttachmentPreview: React.FC<AttachmentPreviewProps> = ({
  attachments,
  onRemove,
}) => {
  const { theme } = useTheme();

  if (attachments.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        px: 1.5,
        py: 1,
        borderTop: `1px solid ${theme.stroke}`,
      }}
    >
      {attachments.map((att, idx) => (
        <Box
          key={`preview-${idx}`}
          sx={{ position: 'relative', display: 'inline-flex' }}
        >
          {att.type === 'photo' ? (
            <Box
              component="img"
              src={att.uri}
              alt={`Pending attachment ${idx + 1}`}
              sx={{
                width: 56,
                height: 56,
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Chip
              label={att.fileName ?? 'File'}
              size="small"
              sx={{
                height: 56,
                borderRadius: '8px',
                backgroundColor: theme.background3,
                color: theme.text,
                px: 1,
              }}
            />
          )}
          <IconButton
            size="small"
            onClick={() => onRemove(idx)}
            aria-label={`Remove attachment ${idx + 1}`}
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: 20,
              height: 20,
              backgroundColor: theme.primary,
              color: theme.textCTA,
              '&:hover': {
                backgroundColor: theme.primaryDark,
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>
      ))}
    </Box>
  );
};

export default AttachmentPreview;
